import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  AppBar,
  Toolbar,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Business,
  Search,
  CalendarToday,
  LocationOn,
  ArrowForward,
  FilterList,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.paceestate.com';

const PublicSales = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [availableCities, setAvailableCities] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSales, setTotalSales] = useState(0);
  const perPage = 12;

  useEffect(() => {
    fetchSales();
    fetchCities();
  }, [page, cityFilter]);

  const fetchSales = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: perPage,
      };

      if (cityFilter) {
        params.city = cityFilter;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await axios.get(`${API_URL}/api/public/sales`, { params });
      setSales(response.data.sales || []);
      setTotalSales(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/sales/cities`);
      setAvailableCities(response.data.cities || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchSales();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatSaleDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSaleStatus = (sale) => {
    const today = new Date();
    const startDate = new Date(sale.start_date);
    const endDate = new Date(sale.end_date);

    if (today < startDate) {
      return { label: 'Upcoming', color: 'info' };
    } else if (today >= startDate && today <= endDate) {
      return { label: 'Active Now', color: 'success' };
    } else {
      return { label: 'Ended', color: 'default' };
    }
  };

  return (
    <Box>
      {/* Header */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Business sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate('/')}>
            PaceEstate
          </Typography>
          <Button color="inherit" onClick={() => navigate('/')}>
            Home
          </Button>
          <Button color="inherit" onClick={() => navigate('/login')} sx={{ ml: 2 }}>
            Staff Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #003366 0%, #1a4d7a 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            Upcoming Estate Sales
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Browse our upcoming estate sales and find unique treasures
          </Typography>
        </Container>
      </Box>

      <Container sx={{ py: 6 }}>
        {/* Search and Filters */}
        <Card sx={{ mb: 4, p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by name, location, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by City</InputLabel>
                <Select
                  value={cityFilter}
                  label="Filter by City"
                  onChange={(e) => {
                    setCityFilter(e.target.value);
                    setPage(1);
                  }}
                  startAdornment={<FilterList sx={{ mr: 1, color: 'text.secondary' }} />}
                >
                  <MenuItem value="">All Cities</MenuItem>
                  {availableCities.map((city) => (
                    <MenuItem key={`${city.city}-${city.state}`} value={city.city}>
                      {city.city}, {city.state} ({city.count})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSearch}
                startIcon={<Search />}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Card>

        {/* Results Summary */}
        <Box mb={3}>
          <Typography variant="h6" color="text.secondary">
            {loading ? 'Loading...' : `${totalSales} ${totalSales === 1 ? 'sale' : 'sales'} found`}
          </Typography>
        </Box>

        {/* Sales Grid */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={60} />
          </Box>
        ) : sales.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {sales.map((sale) => {
                const status = getSaleStatus(sale);
                return (
                  <Grid item xs={12} sm={6} md={4} key={sale.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 8,
                          transform: 'translateY(-4px)',
                          transition: 'all 0.3s ease-in-out',
                        },
                      }}
                      onClick={() => navigate(`/public/sales/${sale.id}`)}
                    >
                      {sale.primary_photo ? (
                        <CardMedia
                          component="img"
                          height="240"
                          image={`${API_URL}${sale.primary_photo.file_path}`}
                          alt={sale.name}
                          sx={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 240,
                            bgcolor: 'grey.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Business sx={{ fontSize: 80, color: 'grey.400' }} />
                        </Box>
                      )}
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                          <Typography variant="h6" fontWeight="bold" sx={{ flex: 1, mr: 1 }}>
                            {sale.name}
                          </Typography>
                          <Chip label={status.label} color={status.color} size="small" />
                        </Box>

                        {sale.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={2}
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {sale.description}
                          </Typography>
                        )}

                        <Box display="flex" flexDirection="column" gap={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarToday fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {formatSaleDate(sale.start_date)}
                            </Typography>
                          </Box>
                          {sale.end_date && sale.end_date !== sale.start_date && (
                            <Box display="flex" alignItems="center" gap={1} pl={3.5}>
                              <Typography variant="body2" color="text.secondary">
                                to {formatSaleDate(sale.end_date)}
                              </Typography>
                            </Box>
                          )}
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocationOn fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {sale.address}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1} pl={3.5}>
                            <Typography variant="body2" color="text.secondary">
                              {sale.city}, {sale.state} {sale.zip_code}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          endIcon={<ArrowForward />}
                          onClick={() => navigate(`/public/sales/${sale.id}`)}
                        >
                          View Sale Details
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={6}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </>
        ) : (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <Business sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No sales found
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={3}>
                {searchTerm || cityFilter
                  ? 'Try adjusting your search filters'
                  : 'Check back soon for upcoming estate sales'}
              </Typography>
              {(searchTerm || cityFilter) && (
                <Button
                  variant="contained"
                  onClick={() => {
                    setSearchTerm('');
                    setCityFilter('');
                    setPage(1);
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Newsletter Signup */}
        <Card sx={{ mt: 6, p: 4, background: 'linear-gradient(135deg, #003366 0%, #1a4d7a 100%)', color: 'white' }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Get Notified of Upcoming Sales
              </Typography>
              <Typography variant="body1">
                Sign up to receive email notifications about new estate sales in your area
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} textAlign={{ xs: 'left', md: 'right' }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => {
                  // TODO: Implement newsletter signup modal
                  alert('Newsletter signup coming soon!');
                }}
              >
                Sign Up Now
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 4, backgroundColor: 'primary.main', color: 'white', mt: 6 }}>
        <Container>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                © {new Date().getFullYear()} PaceEstate. All rights reserved.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} textAlign={{ xs: 'left', md: 'right' }}>
              <Button color="inherit" onClick={() => navigate('/')}>
                Home
              </Button>
              <Button color="inherit" onClick={() => navigate('/login')}>
                Staff Login
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicSales;
